import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LaneData } from '../models/Models';
import { Translation } from '../helper/WinfireLiveConstHelper';
import './ShootOffBanner.css';

/** Must match the `sob-slide-out` animation duration in ShootOffBanner.css */
const SLIDE_OUT_DURATION_MS = 500;

/**
 * What the final screen shows of a shoot-off, derived by FinalCompetitionView from the server state.
 *   - running:  the tied lanes are still firing shoot-off shots
 *   - finished: the tie is broken; the loser is flagged isEliminated in laneData
 */
export interface ShootOffView {
    status: 'running' | 'finished';
    /** Running: the lanes still in the shoot-off. Finished: all lanes of the tie. */
    laneNumbers: number[];
    round: number;
}

interface ShootOffBannerProps {
    /** The shoot-off to announce, or null when there is none (the banner slides out). */
    view: ShootOffView | null;
    /** All lane data — used to look up participant names and the eliminated lane. */
    laneData: LaneData[] | undefined | null;
}

/**
 * Sports-TV style shoot-off announcement banner.
 *
 * Slides in from the top when a shoot-off starts and slides out when the view becomes null.
 *
 * Displays:
 *   - running:  "SHOOT-OFF" headline + round number, chips of the tied participants
 *   - finished: "SHOOT-OFF FINISHED" headline + the eliminated participant, chips coloured
 *               red (eliminated) / green (survived)
 */
const ShootOffBanner: React.FC<ShootOffBannerProps> = ({ view, laneData }) => {
    const { t } = useTranslation();

    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Snapshot of the last shown view — used during slide-out so the banner keeps its content.
    const [snapshot, setSnapshot] = useState<ShootOffView>({ status: 'running', laneNumbers: [], round: 1 });

    // Stable string so the effect only re-runs when the view actually changes. The arrays from
    // SignalR are new on every push, so their references would always look changed.
    const viewKey = view
        ? `${view.status}|${view.round}|${view.laneNumbers.slice().sort((a, b) => a - b).join(',')}`
        : '';

    useEffect(() => {
        if (view) {
            // Cancel any pending unmount and show the banner
            if (leaveTimerRef.current) {
                clearTimeout(leaveTimerRef.current);
                leaveTimerRef.current = null;
            }
            setSnapshot(view);
            setIsLeaving(false);
            setIsVisible(true);
        } else if (isVisible) {
            // Shoot-off is over — play slide-out then unmount
            setIsLeaving(true);
            leaveTimerRef.current = setTimeout(() => {
                setIsVisible(false);
                setIsLeaving(false);
            }, SLIDE_OUT_DURATION_MS); // must match sob-slide-out duration in ShootOffBanner.css
        }
        return () => {
            if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewKey]);

    if (!isVisible) return null;

    const isFinished = snapshot.status === 'finished';

    // Participant chips. Outcome colouring only once finished, and only when the lane is known —
    // a missing lane keeps the neutral style instead of a false "survived" green.
    const participants = snapshot.laneNumbers.map(laneNr => {
        const ld = laneData?.find(l => l.laneNumber === laneNr);
        const name = ld?.participantName;
        const label = name ? `${name} (${laneNr})` : `${t(Translation.LANE)} ${laneNr}`;
        let chipClass = 'sob-participant-chip';
        if (isFinished && ld !== undefined) {
            chipClass += ld.isEliminated ? ' sob-participant-chip--eliminated' : ' sob-participant-chip--survived';
        }
        return { label, name: name || `${t(Translation.LANE)} ${laneNr}`, chipClass, laneNr, isEliminated: ld?.isEliminated === true };
    });

    const eliminatedNames = participants.filter(p => p.isEliminated).map(p => p.name);

    const subtitle = isFinished
        ? eliminatedNames.map(name => t(Translation.SHOOTOFF_ELIMINATED, { name })).join(' · ')
        : `${t(Translation.SHOOTOFF_ROUND, { round: snapshot.round })}${snapshot.round > 1 ? ` — ${t(Translation.SHOOTOFF_PARTICIPANTS)}` : ''}`;

    const bannerClass = 'sob-banner'
        + (isFinished ? ' sob-banner--finished' : '')
        + (isLeaving ? ' sob-banner--leaving' : '');

    return (
        <div className="sob-overlay" role="alert" aria-live="assertive">
            {/* key: re-mounting on the status change replays the entry animation of the new state */}
            <div key={snapshot.status} className={bannerClass}>
                <span className="sob-icon" aria-hidden="true">{isFinished ? '🏁' : '🔥'}</span>

                <div className="sob-text">
                    <span className="sob-title">
                        {isFinished ? t(Translation.SHOOTOFF_FINISHED) : t(Translation.PHASE_SHOOTOFF)}
                    </span>
                    {subtitle && (
                        <span className={`sob-subtitle${isFinished ? ' sob-subtitle--eliminated' : ''}`}>{subtitle}</span>
                    )}
                </div>

                {participants.length > 0 && (
                    <div className="sob-participants" aria-label={t(Translation.SHOOTOFF_PARTICIPANTS)}>
                        {participants.map(({ label, chipClass, laneNr }) => (
                            <span key={laneNr} className={chipClass}>{label}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShootOffBanner;
